import { useCallback, useEffect, useMemo, useState } from 'react';
import useAuth from '@/hooks/useAuth';
import { getEntityConfig, useEntityConfig } from '@/hooks/useEntityConfig';
import { axiosPrivate } from '@/lib/axios';
import { CertificatesList } from './Modules/CertificatesList';

export interface CertificateWithdrawal {
  amount: number;
  client_id: number;
  data_source_unique_id: string;
  entity: string;
  meta: Record<string, any>;
  uuid: string;
}

interface CertificatesApiParams {
  client_id: number;
  entity: string;
}

class CertificatesService {
  private config = getEntityConfig();

  async fetchCertificates(params: CertificatesApiParams): Promise<CertificateWithdrawal[]> {
    const response = await axiosPrivate.post('/api/v1/certificates/list', params, {
      baseURL: this.config.VITE_URL_BIZTECH,
    });

    return response.data;
  }

  async fetchCertificatesIB(params: CertificatesApiParams): Promise<CertificateWithdrawal[]> {
    const response = await axiosPrivate.post('/api/v1/certificates-ib/list', params, {
      baseURL: this.config.VITE_URL_BIZTECH,
    });

    return response.data;
  }

  get entity(): string {
    return this.config.VITE_ENTITY;
  }
}

const useCertificates = () => {
  const { client } = useAuth();
  const config = useEntityConfig();
  const certificatesService = useMemo(() => new CertificatesService(), []);

  const [certificates, setCertificates] = useState<CertificateWithdrawal[]>([]);
  const [certificatesIB, setCertificatesIB] = useState<CertificateWithdrawal[]>([]);
  const [loading, setLoading] = useState(false);
  const clientId = client?.client_details?.id;

  const fetchCertificates = useCallback(async () => {
    const entity = certificatesService.entity;

    if (config.IS_SHOW_CERTIFICATES_DEBUG_MODE) {
      const { default: debugData } = await import('./certificates-debug.json');
      setCertificates(debugData.certificates);
      setCertificatesIB(debugData.certificatesIB);
      setLoading(false);

      return;
    }

    if (!clientId || !entity) {
      setCertificates([]);
      setCertificatesIB([]);
      setLoading(false);

      return;
    }

    setLoading(true);

    try {
      const params: CertificatesApiParams = {
        client_id: clientId,
        entity: entity,
      };

      //TODO: TEMPORATRI DISABLED
      // const certificatesData = await certificatesService.fetchCertificates(params);
      // setCertificates(certificatesData);
      setCertificates([]);

      const certificatesIBData = await certificatesService.fetchCertificatesIB(params);
      setCertificatesIB(certificatesIBData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      console.error('Failed to fetch certificates:', error);
    } finally {
      setLoading(false);
    }
  }, [certificatesService, clientId, config.IS_SHOW_CERTIFICATES_DEBUG_MODE]);

  return {
    certificates,
    certificatesIB,
    loading,
    fetchCertificates,
  };
};

export const CertificatesLayout = () => {
  const { certificates, certificatesIB, loading, fetchCertificates } = useCertificates();

  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  if (loading) {
    return <></>;
  }

  return (
    <>
      <div className="">
        <CertificatesList certificates={certificates} certificatesIB={certificatesIB} />
      </div>
    </>
  );
};
